import { useEffect, useRef, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { copyText, downloadJson } from '@/ui/clipboard';
import { CLASS_CHAT_PROMPT } from '@/constants/instructions';
import { APP_BUILD } from '@/constants/app';
import { exportBackup, importBackup } from '@/db/repo';
import { formatBytes, requestPersistentStorage, storageEstimate } from '@/db/persist';
import { Banner } from '@/ui/components/bits';
import { presetsFor } from '@/domain/exam';
import { signIn, signOut } from '@/sync/client';
import { pinnedBytes, pinnedIds, unpinAll } from '@/library/library';
import { LIBRARY_FILE_CAP_BYTES, UPLOAD_CHUNK_BYTES } from '@/constants/app';

export function SettingsScreen() {
  const { local, prefs, updateLocal, updatePrefs, reload, toast } = useAppState();
  const fileInput = useRef<HTMLInputElement>(null);
  const [estimate, setEstimate] = useState<{ usage: number; quota: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void storageEstimate().then(setEstimate);
  }, []);

  async function askPersist() {
    const state = await requestPersistentStorage();
    await updateLocal({ persistentStorage: state });
    toast(
      state === 'granted'
        ? 'Storage is now persistent.'
        : state === 'denied'
          ? 'The browser declined. Keep backups.'
          : 'This browser does not report storage persistence.',
    );
  }

  async function doExport() {
    setBusy(true);
    try {
      const backup = await exportBackup();
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJson(`chem-bank-backup-${stamp}.json`, backup);
      toast('Backup downloaded.');
    } finally {
      setBusy(false);
    }
  }

  async function doImport(file: File) {
    setBusy(true);
    try {
      const parsed = JSON.parse(await file.text());
      const result = await importBackup(parsed);
      await reload();
      toast(`Restored ${result.restored} records, skipped ${result.skipped} older ones.`);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <h1>Settings</h1>

      <h2 className="section-title">Appearance</h2>
      <div className="switch-row">
        <span>Theme</span>
        <div className="seg">
          {(['system', 'light', 'dark'] as const).map((value) => (
            <button
              key={value}
              className="seg__btn"
              aria-pressed={local?.theme === value}
              onClick={() => void updateLocal({ theme: value })}
            >
              {value[0]!.toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <h2 className="section-title">Study</h2>
      <div className="switch-row">
        <span>
          Cards per cumulative mix
          <br />
          <span className="faint">Default 40.</span>
        </span>
        <input
          className="input mono"
          style={{ width: 90 }}
          type="number"
          min={5}
          max={200}
          value={local?.cumulativeCardCount ?? 40}
          onChange={(e) => void updateLocal({ cumulativeCardCount: Number(e.target.value) || 40 })}
        />
      </div>
      <div className="switch-row">
        <span>
          Questions per cumulative quiz
          <br />
          <span className="faint">Default 20.</span>
        </span>
        <input
          className="input mono"
          style={{ width: 90 }}
          type="number"
          min={5}
          max={200}
          value={local?.cumulativeQuizCount ?? 20}
          onChange={(e) => void updateLocal({ cumulativeQuizCount: Number(e.target.value) || 20 })}
        />
      </div>

      <div className="switch-row">
        <span>
          Difficulty
          <br />
          <span className="faint">
            Auto starts each topic at level 1, moves up when you are getting at least 80% of the
            last eight right, and back down at 40% or below.
          </span>
        </span>
        <div className="seg">
          {(['auto', 1, 2, 3, 'all'] as const).map((value) => (
            <button
              key={String(value)}
              className="seg__btn"
              aria-pressed={local?.difficultyOverride === value}
              onClick={() => void updateLocal({ difficultyOverride: value })}
            >
              {value === 'auto' ? 'Auto' : value === 'all' ? 'All' : ['', 'Easy', 'Med', 'Hard'][value]}
            </button>
          ))}
        </div>
      </div>

      <div className="switch-row">
        <span>
          Focus on latest class
          <br />
          <span className="faint">Puts the newest class first in a cumulative mix.</span>
        </span>
        <button
          className="btn btn--sm"
          aria-pressed={Boolean(local?.focusLatestClass)}
          onClick={() => void updateLocal({ focusLatestClass: !local?.focusLatestClass })}
        >
          {local?.focusLatestClass ? 'On' : 'Off'}
        </button>
      </div>

      <div className="switch-row">
        <span>
          Ask multiple choice blind
          <br />
          <span className="faint">
            A multiple-choice question is shown as a written answer first, with the options hidden
            until you ask for them. Recognising the right option is not the same as knowing the
            answer. The reasoning behind the question is shown either way.
          </span>
        </span>
        <button
          className="btn btn--sm"
          aria-pressed={local?.blindMultipleChoice ?? true}
          onClick={() =>
            void updateLocal({ blindMultipleChoice: !(local?.blindMultipleChoice ?? true) })
          }
        >
          {(local?.blindMultipleChoice ?? true) ? 'On' : 'Off'}
        </button>
      </div>

      <div className="switch-row">
        <span>
          Include the textbook in cumulative review
          <br />
          <span className="faint">
            Off by default — there is far more of the textbook than of your own classes, so mixing it
            in buries what you were actually taught. Textbook chapters are always available on their
            own, and in exams when you pick them.
          </span>
        </span>
        <button
          className="btn btn--sm"
          aria-pressed={Boolean(local?.includeTextbookInCumulative)}
          onClick={() =>
            void updateLocal({ includeTextbookInCumulative: !local?.includeTextbookInCumulative })
          }
        >
          {local?.includeTextbookInCumulative ? 'On' : 'Off'}
        </button>
      </div>

      <h2 className="section-title">Exams</h2>
      <ExamPresetEditor />
      <div className="switch-row">
        <span>
          Allow the Library during exams
          <br />
          <span className="faint">
            Off by default, because your real exams do not allow notes. The periodic table stays
            available either way.
          </span>
        </span>
        <button
          className="btn btn--sm"
          aria-pressed={Boolean(local?.allowLibraryInExams)}
          onClick={() => void updateLocal({ allowLibraryInExams: !local?.allowLibraryInExams })}
        >
          {local?.allowLibraryInExams ? 'Allowed' : 'Blocked'}
        </button>
      </div>

      <h2 className="section-title">Grading</h2>
      <div className="switch-row">
        <span>
          Significant figures
          <br />
          <span className="faint">
            Strict marks a sig-fig mismatch wrong. Lenient warns but counts it correct. This setting
            syncs to your other device. Used by the problem generator from Stage 6.
          </span>
        </span>
        <div className="seg">
          {(['lenient', 'strict'] as const).map((value) => (
            <button
              key={value}
              className="seg__btn"
              aria-pressed={prefs?.sigfigMode === value}
              onClick={() => void updatePrefs({ sigfigMode: value })}
            >
              {value[0]!.toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <h2 className="section-title">Importing</h2>
      <button
        className="btn btn--block"
        onClick={async () => {
          const ok = await copyText(CLASS_CHAT_PROMPT);
          toast(ok ? 'Instructions copied.' : 'Could not reach the clipboard.');
        }}
      >
        Copy instructions
      </button>
      <span className="faint">
        The exact text to paste into a Claude chat before your notes.
      </span>

      <h2 className="section-title">Sync</h2>
      <SyncSection />

      <h2 className="section-title">Backup</h2>
      <div className="stack">
        <button className="btn btn--block" disabled={busy} onClick={() => void doExport()}>
          Export all data
        </button>
        <button className="btn btn--block" disabled={busy} onClick={() => fileInput.current?.click()}>
          Import a backup
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void doImport(file);
            e.target.value = '';
          }}
        />
        <span className="faint">
          A backup is a plain JSON file holding every session, item and piece of progress. Restoring
          keeps whichever copy of a record is newer, so it is safe to restore onto a device that
          already has data. Library files are not included.
        </span>
      </div>

      <h2 className="section-title">Library</h2>
      <LibraryStorageSection />

      <h2 className="section-title">Storage</h2>
      <div className="switch-row">
        <span>
          Persistent storage
          <br />
          <span className="faint">
            {local?.persistentStorage === 'granted'
              ? 'Granted. The browser will not evict your data under pressure.'
              : local?.persistentStorage === 'denied'
                ? 'Denied. Some phone browsers can evict stored data, so export a backup now and then.'
                : 'Not yet requested on this device.'}
          </span>
        </span>
        <button className="btn btn--sm" onClick={() => void askPersist()}>
          Request
        </button>
      </div>
      {estimate ? (
        <p className="faint" style={{ margin: 0 }}>
          Using {formatBytes(estimate.usage)} of about {formatBytes(estimate.quota)} available.
        </p>
      ) : null}

      <h2 className="section-title">Things to know</h2>
      <Banner kind="info">
        <strong>Periodic table charges</strong>
        <div>
          Some valence charges printed on your table look simplified. When Stage 6 lands, check them
          against your course&rsquo;s ion list before relying on them.
        </div>
      </Banner>
      <Banner kind="info">
        <strong>Printed atomic masses</strong>
        <div>
          Your table has several outdated or mistyped masses. The app uses the printed values on
          purpose, because they match what you will have in front of you in an exam, and flags the
          difference when you tap the element.
        </div>
      </Banner>
      <p className="faint" style={{ margin: 0, textAlign: 'center' }}>
        Build <span className="mono">{APP_BUILD}</span>
      </p>
    </div>
  );
}

function LibraryStorageSection() {
  const { toast } = useAppState();
  const [used, setUsed] = useState(0);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setUsed(await pinnedBytes());
    setCount((await pinnedIds()).size);
  };

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="stack">
      <div className="switch-row">
        <span>
          Pinned files on this device
          <br />
          <span className="faint">
            {count === 0
              ? 'Nothing is pinned. Unpinned files need a connection to open.'
              : `${count} file${count === 1 ? '' : 's'}, ${formatBytes(used)}. These open with no connection.`}
          </span>
        </span>
        <button
          className="btn btn--sm"
          disabled={busy || count === 0}
          onClick={async () => {
            setBusy(true);
            try {
              const freed = await unpinAll();
              await refresh();
              toast(`Freed ${formatBytes(freed)}. The files are still in your Library.`);
            } finally {
              setBusy(false);
            }
          }}
        >
          Unpin all
        </button>
      </div>

      <div className="switch-row">
        <span>
          Largest file the Library takes
          <br />
          <span className="faint">
            Files travel in {formatBytes(UPLOAD_CHUNK_BYTES)} pieces, because a Netlify function
            accepts at most 6 MB per request. Chunking is what makes this size workable.
          </span>
        </span>
        <span className="mono">{formatBytes(LIBRARY_FILE_CAP_BYTES)}</span>
      </div>
    </div>
  );
}

function SyncSection() {
  const { local, syncState, syncMessage, pending, syncNow, toast, reload } = useAppState();
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signedIn = Boolean(local?.token) && (local?.tokenExpiresAt ?? 0) > Date.now();

  async function doSignIn() {
    setBusy(true);
    setError(null);
    try {
      await signIn(passcode);
      setPasscode('');
      await reload();
      await syncNow();
      toast('Signed in. Syncing now.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {signedIn ? (
        <>
          <div className="switch-row">
            <span>
              Status
              <br />
              <span className="faint">
                {syncState === 'synced' && 'Everything is up to date.'}
                {syncState === 'syncing' && 'Syncing…'}
                {syncState === 'pending' && `${pending} change${pending === 1 ? '' : 's'} waiting.`}
                {syncState === 'offline' && 'Offline. Your work is saved here and will sync later.'}
                {syncState === 'error' && (syncMessage ?? 'Something went wrong.')}
                {syncState === 'signed-out' && 'Signed out.'}
                {local?.lastSyncAt
                  ? ` Last sync ${new Date(local.lastSyncAt).toLocaleString()}.`
                  : ''}
              </span>
            </span>
            <button className="btn btn--sm" disabled={busy} onClick={() => void syncNow()}>
              Sync now
            </button>
          </div>
          <button
            className="btn btn--block btn--danger-text"
            onClick={async () => {
              await signOut();
              await reload();
              toast('Signed out. Your data stays on this device.');
            }}
          >
            Sign out of sync
          </button>
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Sign in with your passcode to keep this device and your other one in step. Without it
            everything still works, just only here.
          </p>
          <label className="label" htmlFor="passcode">
            Passcode
          </label>
          <input
            id="passcode"
            className="input"
            type="password"
            autoComplete="current-password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && passcode) void doSignIn();
            }}
          />
          {error ? <Banner kind="error">{error}</Banner> : null}
          <button
            className="btn btn--primary btn--block"
            disabled={!passcode || busy}
            onClick={() => void doSignIn()}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </>
      )}

      <Banner kind="warn">
        <strong>About the passcode</strong>
        <div>
          One passcode protects everything, which is convenient but not strong security. Do not
          reuse a password from anywhere else. Anyone who learns it can read and change your study
          data.
        </div>
      </Banner>
      <Banner kind="info">
        <strong>Studying offline on both devices at once</strong>
        <div>
          If you answer the same card offline on your phone and your laptop, the later of the two
          wins and the other Got it or Missed it is lost. Everything else merges cleanly.
        </div>
      </Banner>
    </div>
  );
}

function ExamPresetEditor() {
  const { local, updateLocal } = useAppState();
  const presets = presetsFor(local?.examPresets);

  const change = (id: string, patch: { questions?: number; minutes?: number }) => {
    const next = presets.map((p) => (p.id === id ? { ...p, ...patch } : p));
    void updateLocal({ examPresets: next });
  };

  return (
    <div className="stack">
      {presets.map((preset) => (
        <div key={preset.id} className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ minWidth: 100 }}>{preset.label}</span>
          <div className="row">
            <input
              className="input mono"
              style={{ width: 72 }}
              type="number"
              min={1}
              max={200}
              aria-label={`${preset.label} questions`}
              value={preset.questions}
              onChange={(e) => change(preset.id, { questions: Number(e.target.value) || 1 })}
            />
            <span className="faint">Q</span>
            <input
              className="input mono"
              style={{ width: 72 }}
              type="number"
              min={1}
              max={300}
              aria-label={`${preset.label} minutes`}
              value={preset.minutes}
              onChange={(e) => change(preset.id, { minutes: Number(e.target.value) || 1 })}
            />
            <span className="faint">min</span>
          </div>
        </div>
      ))}
      <button className="btn btn--sm btn--ghost" onClick={() => void updateLocal({ examPresets: [] })}>
        Reset to defaults
      </button>
    </div>
  );
}
